import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, message, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../context/UserContext';
import { useFullScreenLoader } from '../../context/fullScreenLoaderContext';
import { adminGroup } from '../../utils/permissionUtils';
import { Country } from '../../types/commonTypes';
import { notifyMessages } from '../../utils/constants';
import CommonServices from '../../services/commonServices';
import { getCountryLabel } from '../../utils/functions';
import ReactCountryFlag from 'react-country-flag';
import {
    resolveCountryIDFromUser,
    setLastSelectedCountryID,
    syncLastSelectedCountryFromUser,
} from '../../utils/lastSelectedCountryStore';

const { Text } = Typography;

const CountrySwitcher = () => {
    const { user } = useUserAuth();
    const { setLoading } = useFullScreenLoader();
    const userCountries = useSelector((state: any) => state.utils.userCountries) as Country[];
    const { t } = useTranslation();


    if (!user || !userCountries?.length) return null;

    const availableCountries = userCountries.filter((country) => country.enabled);

    if (!availableCountries.length) return null;

    const activeCountryID = resolveCountryIDFromUser(user);
    const selectedCountry = availableCountries.find((country) => country.id === activeCountryID);
    const displayLabel = selectedCountry?.code ?? user.lastSelectedCountryCode ?? user.countryCode ?? t('users.country');

    const items: MenuProps['items'] = availableCountries.map((country) => ({
        key: country.id,
        label: getCountryLabel(country)
    }));

    const handelCountryChange: MenuProps['onClick'] = async ({ key }) => {
        if (key === activeCountryID) return;

        const payload = {
            countryID: key,
            userID: user.id,
        };

        setLastSelectedCountryID(key);

        setLoading(true);
        try {
            const respData = await CommonServices.setSelectedCountry(payload);

            if (respData.success) {
                message.success(notifyMessages.success);
                window.location.replace('/');
                return;
            }
            syncLastSelectedCountryFromUser(user);
            message.error(notifyMessages.errorAccured);
        } catch (error) {
            syncLastSelectedCountryFromUser(user);
            message.error(notifyMessages.errorAccured);
        }
        finally {
            setLoading(false);
        }
    };

    return (
        <Dropdown
            menu={{
                items,
                selectable: true,
                selectedKeys: activeCountryID ? [activeCountryID] : [],
                onClick: handelCountryChange,
            }}
        >
            <Button type="text">
                <Space>
                    <Text style={{ color: '#ffffff' }}>Country:</Text>
                    <ReactCountryFlag countryCode={displayLabel} svg />
                    <Text style={{ color: '#ffffff' }}>{displayLabel}</Text>
                    <DownOutlined style={{ color: '#ffffff' }} />
                </Space>
            </Button>
        </Dropdown>
    );
};

export default CountrySwitcher;
